import React, { useState, useEffect } from 'react'
import { X, Calendar, ChevronDown } from 'lucide-react'
import employeeService from '../../services/employeeService'
import branchService from '../../services/branchService'
import jobService from '../../services/jobService'

const EditTargetModal = ({ isOpen, target, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'sales',
    targetValue: '',
    bonusAmount: '',
    targetMonth: '',
    selectedEmployees: []
  })

  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [jobs, setJobs] = useState([])
  const [filters, setFilters] = useState({
    branch: '',
    job: ''
  })
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    if (isOpen && target) {
      loadDropdownData()
    }
  }, [isOpen, target])

  useEffect(() => {
    if (target) {
      populateFormData()
    }
  }, [target])

  const populateFormData = React.useCallback(() => {
    if (target) {
      try {
        const targetMonth = target.targetMonth ? 
          new Date(target.targetMonth).toISOString().slice(0, 7) : ''
        
        // Debug logging to understand the data structure
        console.log('Target data:', target)
        console.log('Target employees:', target.employees)
        
        let selectedEmployees = []
        
        if (target.employees && Array.isArray(target.employees)) {
          selectedEmployees = target.employees
            .filter(emp => {
              // Filter out null/undefined items and ensure employee exists
              if (!emp) {
                console.warn('Found null/undefined employee item:', emp)
                return false
              }
              if (!emp.employee && !emp._id) {
                console.warn('Employee item missing employee reference:', emp)
                return false
              }
              return true
            })
            .map(emp => {
              try {
                return {
                  employee: emp.employee?._id || emp.employee || emp._id,
                  branch: emp.branch?._id || emp.branch || emp.employee?.branch?._id || emp.employee?.branch
                }
              } catch (error) {
                console.error('Error processing employee item:', emp, error)
                return null
              }
            })
            .filter(emp => emp !== null) // Remove any failed mappings
        }

        setFormData({
          name: target.name || '',
          type: target.type || 'sales',
          targetValue: target.targetValue?.toString() || '',
          bonusAmount: target.bonusAmount?.toString() || '',
          targetMonth: targetMonth,
          selectedEmployees: selectedEmployees
        })
      } catch (error) {
        console.error('Error populating form data:', error)
        // Set safe defaults if there's an error
        setFormData({
          name: target.name || '',
          type: target.type || 'sales',
          targetValue: target.targetValue?.toString() || '',
          bonusAmount: target.bonusAmount?.toString() || '',
          targetMonth: target.targetMonth ? new Date(target.targetMonth).toISOString().slice(0, 7) : '',
          selectedEmployees: []
        })
      }
    }
  }, [target])

  const loadDropdownData = async () => {
    try {
      setLoadingData(true)
      const [employeesResponse, branchesResponse, jobsResponse] = await Promise.all([
        employeeService.getAllEmployees(),
        branchService.getAllBranches(),
        jobService.getAllJobs()
      ])

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data.employees || [])
      }
      if (branchesResponse.success) {
        setBranches(branchesResponse.data.branches || [])
      }
      if (jobsResponse.success) {
        setJobs(jobsResponse.data.jobs || [])
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    } finally {
      setLoadingData(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleEmployeeSelect = (employee, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        selectedEmployees: [...prev.selectedEmployees, {
          employee: employee._id,
          branch: employee.branch._id || employee.branch
        }]
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        selectedEmployees: prev.selectedEmployees.filter(emp => emp.employee !== employee._id)
      }))
    }
  }

  const isEmployeeSelected = (employeeId) => {
    return formData.selectedEmployees.some(emp => emp.employee === employeeId)
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all filtered employees with safety checks
      const allEmployees = filteredEmployees
        .filter(employee => employee && employee._id) // Ensure employee exists
        .map(employee => ({
          employee: employee._id,
          branch: employee.branch?._id || employee.branch || ''
        }))
      setFormData(prev => ({
        ...prev,
        selectedEmployees: allEmployees
      }))
    } else {
      // Deselect all
      setFormData(prev => ({
        ...prev,
        selectedEmployees: []
      }))
    }
  }

  const isAllSelected = () => {
    return filteredEmployees.length > 0 && 
           filteredEmployees.every(employee => isEmployeeSelected(employee._id))
  }

  const isPartiallySelected = () => {
    return filteredEmployees.some(employee => isEmployeeSelected(employee._id)) && 
           !isAllSelected()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const targetData = {
        name: formData.name,
        type: formData.type,
        targetValue: parseFloat(formData.targetValue),
        bonusAmount: parseFloat(formData.bonusAmount),
        targetMonth: formData.targetMonth,
        employees: formData.selectedEmployees
      }

      await onSubmit(targetData)
    } catch (error) {
      console.error('Error updating target:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFilters({ branch: '', job: '' })
    onClose()
  }

  // Filter employees based on selected branch and job
  const filteredEmployees = employees.filter(employee => {
    const matchesBranch = !filters.branch || 
      (employee.branch?._id === filters.branch || employee.branch === filters.branch)
    const matchesJob = !filters.job || 
      (employee.job?._id === filters.job || employee.job === filters.job)
    return matchesBranch && matchesJob
  })

  if (!isOpen || !target) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Edit Target</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-6 custom-scrollbar min-h-0">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="5000 Bonus for October Sales"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start month *
                </label>
                <div className="relative">
                  <input
                    type="month"
                    value={formData.targetMonth}
                    onChange={(e) => handleInputChange('targetMonth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  {/* <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" /> */}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Type *
                </label>
                <div className="relative">
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none pr-10"
                    required
                  >
                    <option value="sales">Sales</option>
                    <option value="orders">Orders</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Value *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.targetValue}
                  onChange={(e) => handleInputChange('targetValue', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bonus Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.bonusAmount}
                  onChange={(e) => handleInputChange('bonusAmount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Employee Selection */}
            <div className="flex flex-col min-h-0">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Employee(s)</h3>
              
              {/* Employee Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 flex-shrink-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">All Branch</label>
                  <div className="relative">
                    <select
                      value={filters.branch}
                      onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none pr-10"
                    >
                      <option value="">All Branch</option>
                      {branches.map(branch => (
                        <option key={branch._id} value={branch._id}>{branch.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">All Jobs</label>
                  <div className="relative">
                    <select
                      value={filters.job}
                      onChange={(e) => setFilters(prev => ({ ...prev, job: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none pr-10"
                    >
                      <option value="">All Jobs</option>
                      {jobs.map(job => (
                        <option key={job._id} value={job._id}>{job.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Employee List */}
              <div className="flex-1 min-h-0">
                {loadingData ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                    <span className="ml-2 text-gray-500">Loading employees...</span>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg h-64 overflow-y-auto custom-scrollbar">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isAllSelected()}
                              ref={(el) => {
                                if (el) el.indeterminate = isPartiallySelected()
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="ml-2">Select</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Job
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEmployees.map((employee) => (
                        <tr key={employee._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isEmployeeSelected(employee._id)}
                              onChange={(e) => handleEmployeeSelect(employee, e.target.checked)}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {employee._id.slice(-8).toUpperCase()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {employee.fullName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {employee.job?.name || employee.job || 'N/A'}
                          </td>
                        </tr>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                            No employees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingData || formData.selectedEmployees.length === 0}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Target'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditTargetModal
